/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import Audio from 'components/lib/Audio';
import ContinueBtn from 'components/lib/ContinueBtn/continueBtn_v2';
import EditableBtn from 'components/lib/EditableBtn';
import EditableText from 'components/lib/EditableText';
import Embed from 'components/lib/Embed';
import Image from 'components/lib/Image';
import Media from 'components/lib/Media';
import PDF from 'components/lib/PDF';
import { OpenPDF } from 'components/lib/PDF/OpenPDF';
import SVG from 'components/lib/SVG';
import Video from 'components/lib/Video';
import Modal from 'components/UI/Modal';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import renderBlock from 'services/apiHelpers/renderBlock';
import checkReturnToInteractionEditing from 'services/checkReturnToInteractionEditing';
import { findByKeyVal_V2 } from 'services/findByKeyVal';
import { State } from 'store/store';
import { InteractionContext } from '../../Layouts/ContainerLogical';
import { VisuallyHidden } from 'components/UI/VisuallyHidden';
import { ImageContext } from 'components/lib/Image';

const maxWidth = 512;

const useFreshProps = id => {
  return useSelector((state: State) => {
    return state.blocks[id].props;
  });
};

const Feedback = ({
  id,
  block,
  correct,
  attempt,
  numberOfAttempts,
  showAttemptNumber = true,
  useMediaInFeedback = true,
  onOpened,
  onClose,
  showCorrectAnswer,
  navigationBehaviour,
}: {
  id?: any;
  block?: any;
  correct: boolean | string;
  useAttemptFeedback?: boolean;
  attempt?: number;
  numberOfAttempts?: number;
  showAttemptNumber?: boolean;
  useMediaInFeedback?: boolean;
  onOpened?: () => void;
  onClose?: any;
  showCorrectAnswer?: any;
  navigationBehaviour?: any;
}) => {
  const ctx = useContext(InteractionContext) as any;

  //   <ImageContext.Provider
  //   value={{
  //     editWrapperType: 0,
  //     editWrapperSize: '100%',
  //     // placeholder: modalPlaceholder,
  //     padding: 5,
  //   }}
  // >
  // </ImageContext.Provider>

  const Components = {
    Feedback: ({ children }) => children,
    Modal: ({ children }) => children,
    ModalWithEditableBtn: ({ children }) => children,
    Correct: ({ children }: { children?: any; props?: any }) => children,
    Incorrect: ({ children }) => children,
    Attempt: ({ children }) => children,
    Image: ({ children, ...props }) => {
      let freshProps = useFreshProps(props.id);
      freshProps = {
        ...props,
        ...freshProps,
      };

      return (
        <Image
          {...{
            ...freshProps,
            // usePlaceholderValue: true,
            // maxHeight: 30,
            editWrapperType: 0,
            editWrapperSize: '100%',
            padding: 5,
          }}
        />
      );
    },
    Video: ({ children, ...props }) => {
      let freshProps = useFreshProps(props.id);
      freshProps = {
        ...props,
        ...freshProps,
      };

      return <Video {...freshProps}>{children}</Video>;
    },

    Transcript: ({ children, ...props }) => {
      return children;
    },
    SVG: ({ children, ...props }) => {
      let freshProps = useFreshProps(props.id);
      freshProps = {
        ...props,
        ...freshProps,
      };
      return <SVG {...freshProps} />;
    },
    Embed,
    Media,
    Audio: ({ children, ...props }) => {
      let freshProps = useFreshProps(props.id);
      freshProps = {
        ...props,
        ...freshProps,
      };

      return <Audio {...freshProps}>{children}</Audio>;
    },
    PDF,
    OpenPDF,
    Text: EditableText,
    EditableButton: ({ ...props }) => {
      let freshProps = useFreshProps(props.id);
      freshProps = {
        ...props,
        ...freshProps,
      };

      return <EditableBtn {...freshProps} onClick={onClose} />;
    },
    ContinueButton: ({ ...props }) => {
      let freshProps = useFreshProps(props.id);
      freshProps = {
        ...props,
        ...freshProps,
      };

      return (
        <ContinueBtn
          {...{
            ...freshProps,
            unlocked: true,
            removeable: false,
            disableLastPageMessage: true,
            //
            keyEventsDisabled: true,
          }}
          onClick={() => {}}
        />
      );
    },
  };

  //
  const renderedBlock = useMemo(() => {
    return renderBlock(
      block || findByKeyVal_V2(id, 'type', 'Feedback'),
      Components
    ) as any;
  }, []);

  const Feedbacks = renderedBlock.props.children;

  const Correct_Feedback = Feedbacks[0];
  const Incorrect_Feedback = Feedbacks[1];

  const Attempt_Feedback =
    Feedbacks[2].props.type === 'Attempt_Feedback'
      ? Feedbacks[2]
      : Feedbacks[1];

  const FeedbackButtonCorrect = Correct_Feedback.props.children[2];
  const FeedbackButtonIncorrect = Incorrect_Feedback.props.children[2];

  const buttonContainer = useRef<HTMLDivElement>(null);

  const parsedOnClose = () => {
    if (window.dev()) {
      checkReturnToInteractionEditing(true);
      onClose();
      return;
    }

    onClose();

    if (correct === 'attempt') {
      return;
    }

    if (!correct && attempt && attempt < numberOfAttempts!) {
      return;
    }

    // this code has been updated since
    // from here the correct / incorrect <a> tags ( buttons ) navigate to their hrefs
  };

  let img;
  let text;
  let button;
  let targetFeedbackElements;

  if (correct === 'attempt') {
    targetFeedbackElements = Attempt_Feedback.props.children;
  } else {
    targetFeedbackElements = Feedbacks[correct ? 0 : 1].props.children;
  }

  img = targetFeedbackElements[0];
  text = targetFeedbackElements[1];
  button = targetFeedbackElements[2];

  // reassign button
  if (correct !== 'attempt') {
    if (FeedbackButtonCorrect && FeedbackButtonIncorrect) {
      button = correct ? FeedbackButtonCorrect : FeedbackButtonIncorrect;
    } else {
      // for ' legacy ' feedback that won't have FeedbackButtonCorrect || FeedbackButtonIncorrect
      button = renderedBlock.props.children[2];
    }
  }

  if (!button) {
    button = renderedBlock.props.children[2];
  }

  const showAttemptNumberComputed =
    showAttemptNumber &&
    correct !== true &&
    numberOfAttempts! > 1 &&
    attempt! < numberOfAttempts!;

  let nullSpaceCloseAction = () => {};

  if (button.props.location) {
    if (
      button.props.location === 'next' ||
      (numberOfAttempts! > 1 && attempt! < numberOfAttempts!)
    ) {
      if (correct !== true) {
        nullSpaceCloseAction = onClose;
      }
    }
  } else {
    nullSpaceCloseAction = onClose;
  }

  const feedbackText = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!feedbackText.current) {
      return;
    }

    // this is wrong. just testing something
    const chameleonText = feedbackText.current.querySelector(
      '.chameleon-text'
    ) as HTMLDivElement;

    chameleonText?.removeAttribute('tabindex');
  });

  useEffect(() => {
    if (!buttonContainer.current) {
      return;
    }

    const anchorButton = buttonContainer.current.querySelector('a');

    if (!anchorButton) {
      return;
    }

    anchorButton.style.margin = '0';

    const killKeyAndPointerEvents = () => {
      // we've taken the href off the anchorButton to prevent key events from firing
      anchorButton.href = '';
      anchorButton.style.pointerEvents = 'none';
    };

    if (!correct && attempt && attempt < numberOfAttempts!) {
      killKeyAndPointerEvents();
      return;
    }

    if (correct && navigationBehaviour.correct) {
      killKeyAndPointerEvents();
      return;
    }

    if (!correct && navigationBehaviour.incorrect) {
      killKeyAndPointerEvents();
      return;
    }
  });

  return (
    <Modal
      disableNullSpaceClick={window.dev() ? false : true}
      onOpened={onOpened}
      onClose={window.dev() ? onClose : nullSpaceCloseAction}
    >
      <div
        css={css`
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
          height: 100%;
          max-width: 768px;
          padding: 50px;
        `}
      >
        {useMediaInFeedback && (
          <div
            css={css`
              position: relative;
              width: 100%;

              margin-bottom: 30px;

              .chameleon-image img {
                // max-height: 30vh !important;

                height: auto !important;
                object-fit: contain;
              }

              @media (max-width: 768px) {
                width: 100%;
                margin-bottom: 25px;
              }
            `}
          >
            {img}
          </div>
        )}

        {correct === 'attempt' && showAttemptNumberComputed && (
          <div
            style={{
              margin: '0 0 15px 0',
              fontFamily: 'inherit',
              fontWeight: 700,
              color: '#333',
            }}
            aria-hidden="true"
          >
            <p>
              Attempt {attempt} of {numberOfAttempts}
            </p>
          </div>
        )}
        <div
          ref={feedbackText}
          css={css`
            position: relative;
            width: 100%;
            margin-bottom: 30px;
          `}
        >
          {text}
        </div>

        {correct === 'attempt' && showAttemptNumberComputed && (
          <span className="sr-only">
            Attempt {attempt} of {numberOfAttempts}
          </span>
        )}
        <div
          ref={buttonContainer}
          css={css`
            position: relative;
            display: flex;
            justify-self: flex-end;
            justify-content: center;
            width: 100%;
          `}
        >
          <span
            css={css`
              cursor: pointer;

              &:hover {
                opacity: 0.8;
              }

              button {
                pointer-events: none;
              }

              div [class*='NavigationButton'] {
                top: -10px;
                height: calc(100% + 20px);
              }
            `}
            onClick={e => {
              !window.dev() ? parsedOnClose() : null;
            }}
          >
            {button}
          </span>
        </div>
      </div>
    </Modal>
  );
};

export default Feedback;
